import React, { useCallback } from 'react';

import ActiveSwitch from '@/components/ActiveSwitch';
import Input from '@/components/Input';
import { Provider } from '@/models/gen/graphql';
import Table from '@/common/Table';
import { getClasses } from '@/utils';
import { parsePhoneNumber } from '@/utils';
import { updateClientActivationBulk } from '@/api/services/providers/updateClientBulk';

export type ClientsTableRowProps = {
  data: Provider;
  isSelected: boolean;
  onSelect: (id: string) => void;
  onDoubleClick: () => void;
};

/**
 * A single data row in the clients table.
 * Receives its Provider data and selection state as props.
 */
const ClientsTableRow = ({ data, isSelected, onSelect, onDoubleClick }: ClientsTableRowProps): React.JSX.Element => {
  const classes = getClasses(isSelected ? 'selected' : undefined);

  const handleSelectClick = useCallback((): void => onSelect(data.id), [onSelect, data.id]);

  const handleActiveChange = useCallback(
    async (isChecked: boolean): Promise<void> => {
      const input: Pick<Provider, 'id' | 'active'> = {
        id: data?.id,
        active: isChecked ? 1 : 0,
      };
      try {
        const res = await updateClientActivationBulk([input]);
        if (!res) throw new Error('Failed to update Client activation.');
      } catch (err) {
        // Re-throw so ActiveSwitch can revert its UI state
        throw err;
      }
    },
    [data?.id]
  );

  return (
    <Table.Row className={classes} onDoubleClick={onDoubleClick}>
      <Table.Cell className="SELECT">
        <Input type="checkbox" name="SELECT_CLIENT" checked={isSelected} onChange={handleSelectClick} />
      </Table.Cell>
      <Table.Cell className="NAME alternate" data-placeholder="--">
        {data?.name}
      </Table.Cell>
      <Table.Cell className="DISPLAY_NAME" data-placeholder="--">
        {data?.displayName}
      </Table.Cell>
      <Table.Cell className="CONTACT_PERSON alternate" data-placeholder="--">
        {data?.contactPerson}
      </Table.Cell>
      <Table.Cell className="PRIMARY_EMAIL" data-placeholder="--">
        {data?.primaryEmail}
      </Table.Cell>
      <Table.Cell className="PRIMARY_PHONE alternate" data-placeholder="--">
        {data?.primaryPhoneNumber ? parsePhoneNumber(data.primaryPhoneNumber, ' ') : undefined}
      </Table.Cell>
      <Table.Cell className="TYPE" data-placeholder="--">
        {data?.type}
      </Table.Cell>
      <Table.Cell className="ACTIVE">
        <ActiveSwitch checked={!!data?.active} onChange={handleActiveChange} />
      </Table.Cell>
    </Table.Row>
  );
};

export default React.memo(ClientsTableRow);
